
import React from "react";
import { format, addDays, isSameDay, startOfWeek, isPast, endOfWeek, isWithinInterval } from "date-fns";
import { pt, enUS, fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { StudentEvent } from "../types";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";
import { WeekViewHeader } from "./WeekViewHeader";
import { TimeGrid } from "./TimeGrid";
import { CalendarEvent } from "./CalendarEvent";
import { CancelledEventsList } from "./CancelledEventsList";

interface StudentWeekViewProps {
  date: Date;
  settings: any;
  events: StudentEvent[];
  handleDateClick: (date: Date) => void;
  handleEventClick: (event: StudentEvent) => void;
}

export const WeekView = ({
  date,
  settings,
  events = [],
  handleDateClick,
  handleEventClick,
}: StudentWeekViewProps) => {
  const { language } = useLanguage();
  const { t } = useTranslations("schedule");

  if (!settings) return null;

  // Calendar setup
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
    .filter(day => settings.working_days.includes(day.getDay()));
  
  // Log events para depuração
  console.log('Week view events:', events);
  
  // Time slots
  const timeSlots = [];
  const [startHour] = settings.work_start_time.split(':').map(Number);
  const [endHour] = settings.work_end_time.split(':').map(Number);
  
  for (let hour = startHour; hour <= endHour; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
  }

  const gridTemplateColumns = `60px repeat(${weekDays.length}, minmax(0, 1fr))`;

  // Separate active and cancelled events
  const activeEvents = events.filter(event => 
    event.status !== 'cancelled' && event.status !== 'rejected'
  );
  
  // Filter cancelled events for the current week
  const cancelledEvents = events.filter(event => {
    if (!event.isCurrentStudent || (event.status !== 'cancelled' && event.status !== 'rejected')) {
      return false;
    }
    
    const eventDate = new Date(event.start_time);
    return isWithinInterval(eventDate, { start: weekStart, end: weekEnd });
  }).sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());

  // Constants for layout
  const HEADER_HEIGHT = 80;
  const HOUR_HEIGHT = 60;
  const TIME_COLUMN_WIDTH = 60;

  // Helper to calculate event vertical position
  const calculateEventPosition = (time: Date) => {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    const totalMinutes = (hours - startHour) * 60 + minutes;
    return HEADER_HEIGHT + (totalMinutes * HOUR_HEIGHT) / 60;
  };

  return (
    <div className="space-y-6">
      <div className="w-full overflow-x-auto">
        <div className="min-w-[800px] relative">
          <div className="grid relative" style={{ gridTemplateColumns }}>
            {/* Calendar Header */}
            <WeekViewHeader weekDays={weekDays} />

            {/* Time Grid */}
            <TimeGrid 
              timeSlots={timeSlots} 
              weekDays={weekDays} 
              handleDateClick={handleDateClick} 
            />

            {/* Active Events */}
            {activeEvents.map((event) => {
              const startTime = new Date(event.start_time);
              const dayIndex = weekDays.findIndex(day => isSameDay(day, startTime));
              if (dayIndex === -1) return null;

              return (
                <CalendarEvent 
                  key={event.id}
                  event={event}
                  weekDays={weekDays}
                  calculateEventPosition={calculateEventPosition}
                  handleEventClick={handleEventClick}
                />
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Cancelled Events List */}
      <CancelledEventsList 
        events={cancelledEvents} 
        handleEventClick={handleEventClick} 
      />
    </div>
  );
};
